/**
 * Portal pracownika — „Wnioski" (spec 03 §8).
 *
 * Kafelki typów pochodzą z konfiguracji tenanta [D2], nie z listy zaszytej
 * w kodzie. Klik otwiera formularz (makieta składała wniosek jednym kliknięciem
 * i tylko optymistycznie — u nas wniosek naprawdę trafia do działu HR).
 *
 * Kafelki oznaczone jako zmiana danych prowadzą do wniosku o zmianę pola akt
 * — tam liczy się porównanie przed/po, a nie payload sprawy.
 *
 * Kafelek „członek rodziny" ma własny formularz (Etap 37): dane z ZUS ZCNA,
 * a dniem poinformowania płatnika jest dzień złożenia wniosku — dlatego żadnego
 * pola „data poinformowania" pracownik nie wypełnia.
 *
 * Fala 8: kafelki zmiany danych są zawsze widoczne, a „Zmiana danych osobowych"
 * pyta najpierw, które pole (grupy 37 i 38). Kafelki urlopowe otwierają kreator
 * urlopu z saldem zamiast ogólnego formularza (grupa 40). „Historia wniosków"
 * zbiera sprawy, wnioski o zmianę danych i wnioski urlopowe — z serwera, bo
 * zakres jest zawsze własny.
 */
import { useId, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Button,
  Calendar,
  Card,
  Document,
  EmptyState,
  FormField,
  Inbox,
  InfoBanner,
  Input,
  Modal,
  Spinner,
  StatusPill,
  useToast,
} from '@k1p4/ui'
import { warsawDay } from '@k1p4/shared'
import { formatDate, formatPLN } from '@k1p4/i18n'
import {
  api,
  type ChangeRequestField,
  type PenaltyItem,
  type PortalChangeRequestItem,
  type PortalLeaveHistoryItem,
  type PortalRequestItem,
  type PortalRequestType,
  type PortalRequestTypeItem,
  type RequestStatus,
} from '../../api/client'
import { ChangeRequestModal } from '../app/ChangeRequestModal'
import { FamilyRequestModal } from './FamilyRequestModal'
import { LeaveRequestModal } from './LeaveRequestModal'
import { PenaltyObjectionModal } from './PenaltyObjectionModal'
import styles from './PortalRequests.module.css'

const STATUS_TONE: Record<RequestStatus, 'warning' | 'success' | 'danger'> = {
  pending: 'warning',
  approved: 'success',
  rejected: 'danger',
}

const LEAVE_TONE: Record<string, 'warning' | 'success' | 'danger'> = {
  pending: 'warning',
  approved: 'success',
  rejected: 'danger',
  change_requested: 'warning',
}

type HistoryEntry =
  | { source: 'request'; createdAt: string; item: PortalRequestItem }
  | { source: 'change'; createdAt: string; change: PortalChangeRequestItem }
  | { source: 'leave'; createdAt: string; leave: PortalLeaveHistoryItem }

const PENALTY_TONE: Record<string, 'pink' | 'warning' | 'success' | 'danger'> = {
  zastosowana: 'warning',
  sprzeciw: 'pink',
  sprzeciw_odrzucony: 'danger',
  sprzeciw_uwzgledniony: 'success',
}

export function PortalRequests() {
  const { t, i18n } = useTranslation()
  const toast = useToast()
  const queryClient = useQueryClient()
  const ids = { from: useId(), to: useId(), note: useId() }

  const [form, setForm] = useState<PortalRequestTypeItem | null>(null)
  const [changeField, setChangeField] = useState<ChangeRequestField | null>(null)
  /* Kafelek z kilkoma polami (zmiana danych osobowych) — najpierw wybór pola. */
  const [pickFields, setPickFields] = useState<readonly ChangeRequestField[] | null>(null)
  /* Kafelek urlopowy — kreator z typem z kafelka (grupa 40). */
  const [leaveType, setLeaveType] = useState<string | null>(null)
  /* Wniosek o członka rodziny: z kafelka albo z wiersza w sekcji rodziny. */
  const [familyOpen, setFamilyOpen] = useState(false)
  const [familyMemberId, setFamilyMemberId] = useState<string | null>(null)
  const [dates, setDates] = useState({ from: '', to: '' })
  const [note, setNote] = useState('')
  const [error, setError] = useState<string | null>(null)

  const types = useQuery({ queryKey: ['request-types'], queryFn: () => api.requestTypes() })
  const mine = useQuery({ queryKey: ['my-requests'], queryFn: () => api.myRequests() })
  /* Formularz zmiany danych potrzebuje id akt i wartości „przed". */
  const changeFields = useQuery({
    queryKey: ['my-change-fields'],
    queryFn: () => api.myChangeFields(),
  })
  /* Kogo firma zgłosiła do ubezpieczenia zdrowotnego (Etap 30) — tylko podgląd. */
  const familyMembers = useQuery({
    queryKey: ['my-family'],
    queryFn: () => api.myFamilyMembers(),
  })
  /* Kary porządkowe i sprzeciw z art. 112 § 1 k.p. (Etap 38). */
  const penalties = useQuery({ queryKey: ['my-penalties'], queryFn: () => api.myPenalties() })

  function closeForm(): void {
    setForm(null)
    setDates({ from: '', to: '' })
    setNote('')
    setError(null)
  }

  /*
   * Sprzeciw wnosi PRACOWNIK i tylko on: siedem dni od zawiadomienia o karze
   * (art. 112 § 1 k.p.). Czy termin jeszcze biegnie, liczy serwer
   * (`objectionOpen`) — w dobie polskiej, nie w UTC przeglądarki.
   */
  const [objectionFor, setObjectionFor] = useState<PenaltyItem | null>(null)

  const submit = useMutation({
    mutationFn: (type: PortalRequestType) =>
      api.submitRequest({
        type,
        ...(dates.from ? { dateFrom: dates.from } : {}),
        ...(dates.to ? { dateTo: dates.to } : {}),
        ...(note.trim() ? { note: note.trim() } : {}),
      }),
    onSuccess: (_result, type) => {
      toast.show(t('portalRequests.submitted', { type: t(`requestTypes.${type}`) }), {
        tone: 'success',
      })
      closeForm()
      void queryClient.invalidateQueries({ queryKey: ['my-requests'] })
    },
    onError: () => setError('portalRequests.errorGeneric'),
  })

  /*
   * Modal zmiany danych renderuje się tylko przy załadowanych changeFields —
   * bez tej bramki klik w kafelek zmiany podczas ładowania (albo po błędzie
   * tego zapytania) nie robiłby nic, bez żadnego komunikatu.
   */
  if (types.isPending || mine.isPending || changeFields.isPending) {
    return <Spinner label={t('common.loading')} />
  }
  if (changeFields.isError) {
    return <EmptyState title={t('common.error')} description={t('common.retry')} />
  }

  const items = mine.data?.items ?? []
  /* Jedna historia z trzech źródeł, najnowsze na górze (spec 03 §8). */
  const history: HistoryEntry[] = [
    ...items.map((item) => ({ source: 'request' as const, createdAt: item.createdAt, item })),
    ...(mine.data?.changes ?? []).map((change) => ({
      source: 'change' as const,
      createdAt: change.createdAt,
      change,
    })),
    ...(mine.data?.leaves ?? []).map((leave) => ({
      source: 'leave' as const,
      createdAt: leave.createdAt,
      leave,
    })),
  ].toSorted((left, right) => right.createdAt.localeCompare(left.createdAt))
  const hasEmployeeRecord = mine.data?.hasEmployeeRecord ?? false
  /*
   * Członka rodziny zgłasza się za ubezpieczonego TEGO płatnika (grupa 166): osoba
   * na B2B, na dziele albo student na zleceniu widzi kafel nieaktywny z powodem
   * — kafel jest ustawowy, więc nie znika (ADR-0038 §4).
   */
  const insured = familyMembers.data?.insured !== false
  /* Wnioski czekające na decyzję — osoba jest „w drodze", zanim trafi na listę (grupa 128). */
  const pendingFamily = items.filter(
    (item) => item.type === 'czlonek_rodziny' && item.status === 'pending' && item.family,
  )
  const rangeInvalid = dates.from !== '' && dates.to !== '' && dates.to < dates.from

  return (
    <>
      {!hasEmployeeRecord ? (
        <InfoBanner tone="warning" className={styles.banner}>
          {t('portalRequests.noEmployeeRecord')}
        </InfoBanner>
      ) : null}

      <section className={styles.section}>
        <h2 className={styles.title}>{t('portalRequests.newTitle')}</h2>
        <p className={styles.subtitle}>{t('portalRequests.newSubtitle')}</p>

        <div className={styles.tiles}>
          {(types.data?.items ?? []).map((item) => (
            <button
              key={item.type}
              type="button"
              className={styles.tile}
              disabled={!hasEmployeeRecord || (item.kind === 'family' && !insured)}
              onClick={() => {
                setError(null)
                const fields = item.fields?.length ? item.fields : item.field ? [item.field] : []
                if (item.kind === 'change' && fields.length > 1) setPickFields(fields)
                else if (item.kind === 'change' && fields.length === 1) setChangeField(fields[0]!)
                else if (item.kind === 'leave') setLeaveType(item.leaveTypeKey ?? 'wypoczynkowy')
                else if (item.kind === 'family') {
                  setFamilyMemberId(null)
                  setFamilyOpen(true)
                } else setForm(item)
              }}
            >
              <span className={styles.tileIcon} aria-hidden="true">
                {item.needsDateRange ? <Calendar size={20} /> : <Document size={20} />}
              </span>
              {t(`requestTypes.${item.type}`)}
            </button>
          ))}
        </div>
      </section>

      {/*
        Członkowie rodziny w ubezpieczeniu zdrowotnym (Etap 30, 37). Zgłoszenie
        i utratę uprawnień składa się wnioskiem; dokument ZUS ZCNA składa
        PŁATNIK, a pracownik widzi, czy już poszedł (grupa 139).
      */}
      <section className={styles.section}>
        <div className={styles.sectionHead}>
          <div>
            <h2 className={styles.title}>{t('family.portalTitle')}</h2>
            {/* Osobie bez tytułu nie obiecujemy ZCNA w 7 dni — mówi to baner. */}
            {insured ? <p className={styles.subtitle}>{t('family.portalLead')}</p> : null}
          </div>
          {/*
            Zgłoszenie i utrata uprawnień idą wnioskiem, bo dokument ZCNA składa
            płatnik — a termin siedmiu dni liczy się od dnia, w którym pracownik
            o tym powiedział (Etap 37).
          */}
          <Button
            size="sm"
            variant="secondary"
            disabled={!hasEmployeeRecord || !insured}
            onClick={() => {
              setFamilyMemberId(null)
              setFamilyOpen(true)
            }}
          >
            {t('family.requestAdd')}
          </Button>
        </div>
        {!insured ? (
          <InfoBanner tone="info" className={styles.banner}>
            {t('family.notInsured')}
          </InfoBanner>
        ) : null}
        {pendingFamily.length > 0 ? (
          <Card pad="flush" className={styles.list}>
            {pendingFamily.map((item) => (
              <div key={item.id} className={styles.row}>
                <span className={styles.rowType}>
                  {item.family!.memberLabel ?? t(`family.actions.${item.family!.action}`)}
                </span>
                <span className={styles.rowMeta}>
                  {t(`family.actions.${item.family!.action}`)}
                  {' · '}
                  {t('family.pendingRequest', {
                    date: formatDate(warsawDay(new Date(item.createdAt)), 'dot'),
                  })}
                </span>
              </div>
            ))}
          </Card>
        ) : null}
        {familyMembers.data === undefined ||
        (familyMembers.data.items.length === 0 && (pendingFamily.length > 0 || !insured)) ? null : familyMembers
            .data.items.length === 0 ? (
          <EmptyState
            title={t('family.portalEmpty')}
            description={t('family.portalEmptyBody')}
          />
        ) : (
          <Card pad="flush" className={styles.list}>
            {familyMembers.data.items.map((member) => (
              <div key={member.id} className={`${styles.row} ${styles.familyRow}`}>
                {/* Nazwa, uprawnienie i stan ZCNA w jednej kolumnie — przycisk ma swoje miejsce. */}
                <div className={styles.familyBody}>
                  <p className={styles.rowType}>
                    {member.firstName} {member.lastName}
                  </p>
                  <p className={styles.rowMeta}>
                    {t(`family.kinship.${member.kinshipCode}`, { defaultValue: member.kinshipName ?? member.kinshipCode })}
                    {' · '}
                    {t('family.from', { date: formatDate(member.entitlementFrom, 'dot') })}
                    {member.entitlementTo
                      ? ` · ${t('family.to', { date: formatDate(member.entitlementTo, 'dot') })}`
                      : ''}
                  </p>
                  {/* Stan ZCNA bez szczegółów zastrzeżeń: czeka albo zgłoszono (grupa 139). */}
                  {(() => {
                    /* Po utracie uprawnień liczy się stan TEGO zgłoszenia, nie pierwszego. */
                    const ending = member.entitlementTo !== null && member.zcnaEnd
                    const state = ending ? member.zcnaEnd : member.zcna
                    if (!state) return null
                    return (
                      <p className={styles.rowMeta}>
                        {state.status === 'sent' && state.sentAt !== null
                          ? t(ending ? 'family.zcnaEndSent' : 'family.zcnaSent', {
                              date: formatDate(warsawDay(new Date(state.sentAt)), 'dot'),
                            })
                          : t(ending ? 'family.zcnaEndPending' : 'family.zcnaPending', {
                              date: formatDate(state.deadline, 'dot'),
                            })}
                      </p>
                    )
                  })()}
                </div>
                {member.entitlementTo === null ? (
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => {
                      setFamilyMemberId(member.id)
                      setFamilyOpen(true)
                    }}
                  >
                    {t('family.requestEnd')}
                  </Button>
                ) : null}
              </div>
            ))}
          </Card>
        )}
      </section>

      {/*
        Kary porządkowe (Etap 38). Pracownik ma prawo wiedzieć, co mu wymierzono
        i do kiedy może wnieść sprzeciw; kary uznane za niebyłe nie wracają na
        tę listę (art. 113 § 1 k.p.).
      */}
      {(penalties.data?.items ?? []).length > 0 ? (
        <section className={styles.section}>
          <h2 className={styles.title}>{t('penalties.portalTitle')}</h2>
          <p className={styles.subtitle}>{t('penalties.portalLead')}</p>
          <Card pad="flush" className={styles.list}>
            {(penalties.data?.items ?? []).map((penalty) => (
              <div key={penalty.id} className={styles.row}>
                <span className={styles.rowType}>
                  {t(`penalties.kinds.${penalty.kind}`)}
                  {' · '}
                  <StatusPill tone={PENALTY_TONE[penalty.status] ?? 'warning'}>
                    {t(`penalties.statuses.${penalty.status}`)}
                  </StatusPill>
                </span>
                <span className={styles.rowMeta}>
                  {t(`penalties.offences.${penalty.offence}`)}
                  {' · '}
                  {t('penalties.offenceOn', { date: formatDate(penalty.offenceOn, 'dot') })}
                  {penalty.amountGr !== null ? ` · ${formatPLN(penalty.amountGr)}` : ''}
                  {penalty.deductedGr > 0
                    ? ` · ${t('penalties.deducted', { amount: formatPLN(penalty.deductedGr) })}`
                    : ''}
                  {penalty.refundedGr > 0
                    ? ` · ${t('penalties.refunded', { amount: formatPLN(penalty.refundedGr) })}`
                    : ''}
                  {penalty.objectionDeadline === null
                    ? ` · ${t('penalties.notNotified')}`
                    : penalty.objectionOpen
                      ? ` · ${t('penalties.objectionUntil', {
                          date: formatDate(penalty.objectionDeadline, 'dot'),
                        })}`
                      : penalty.objectedOn === null
                        ? /* Termin minął bez sprzeciwu — mówimy to, zamiast milczeć (fala 9b). */
                          ` · ${t('penalties.objectionClosed', {
                            date: formatDate(penalty.objectionDeadline, 'dot'),
                          })}`
                        : ''}
                  {penalty.objectionDecidedOn !== null
                    ? ` · ${t('penalties.decidedOn', { date: formatDate(penalty.objectionDecidedOn, 'dot') })}`
                    : ''}
                  {penalty.status === 'sprzeciw' && penalty.objectionSilenceOn !== null
                    ? ` · ${t('penalties.silenceOn', {
                        date: formatDate(penalty.objectionSilenceOn, 'dot'),
                      })}`
                    : ''}
                </span>
                <span className={styles.rowNote}>{penalty.description}</span>
                {penalty.objectionComment !== null ? (
                  <span className={styles.rowNote}>
                    {/*
                      Decyzja sprzed migracji 0063 nadpisała sprzeciw — nie
                      przypisujemy tej treści pracownikowi (fala 9b).
                    */}
                    {t(penalty.objectionCommentLegacy === true ? 'penalties.legacyReason' : 'penalties.objectionReason', {
                      text: penalty.objectionComment,
                    })}
                  </span>
                ) : null}
                {/*
                  Uwzględnienie milczeniem to skutek z mocy ustawy (art. 112 § 1
                  zd. 2), a nie decyzja kadr — mówimy to wprost.
                */}
                {penalty.objectionDecidedBy === 'milczenie' ? (
                  <span className={styles.rowNote}>{t('penalties.bySilenceLong')}</span>
                ) : null}
                {penalty.objectionDecisionComment !== null ? (
                  <span className={styles.rowNote}>
                    {t('penalties.decisionReason', { text: penalty.objectionDecisionComment })}
                  </span>
                ) : null}
                {/* Pouczenie z art. 112 § 2: odrzucony sprzeciw otwiera drogę do sądu pracy. */}
                {penalty.status === 'sprzeciw_odrzucony' ? (
                  <InfoBanner tone="warning">
                    {/* Bez zapisanej chwili zawiadomienia (decyzja sprzed 0063) pouczenie bez daty. */}
                    {penalty.courtDeadline !== null
                      ? t('penalties.courtNotice', { date: formatDate(penalty.courtDeadline, 'dot') })
                      : t('penalties.courtNoticeNoDate')}
                  </InfoBanner>
                ) : null}
                {penalty.objectionOpen ? (
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => setObjectionFor(penalty)}
                  >
                    {t('penalties.object')}
                  </Button>
                ) : null}
              </div>
            ))}
          </Card>
        </section>
      ) : null}

      <section className={styles.section}>
        <h2 className={styles.title}>{t('portalRequests.historyTitle')}</h2>
        {history.length === 0 ? (
          <EmptyState
            icon={<Inbox size={28} />}
            title={t('portalRequests.empty')}
            description={t('portalRequests.emptyBody')}
          />
        ) : (
          <Card pad="flush" className={styles.list}>
            {history.map((entry) => {
              const submittedAt = t('portalRequests.submittedAt', {
                date: formatDate(warsawDay(new Date(entry.createdAt)), 'dot'),
              })
              if (entry.source === 'change') {
                const change = entry.change
                return (
                  <div key={`change-${change.id}`} className={styles.row}>
                    <div>
                      <p className={styles.rowType}>
                        {t('portalRequests.changeTitle', {
                          field: t(`requestFields.${change.fieldKey}`),
                        })}
                      </p>
                      <p className={styles.rowMeta}>
                        {submittedAt}
                        {change.submittedByMe ? '' : ` · ${t('portalRequests.submittedByHr')}`}
                      </p>
                      <p className={styles.rowMeta}>
                        {t('portalRequests.changeValues', {
                          before: change.beforeValue ?? t('portalRequests.noValue'),
                          after: change.afterValue,
                        })}
                      </p>
                      {change.decisionComment ? (
                        <p className={styles.rowNote}>
                          {t('portalRequests.decision', { text: change.decisionComment })}
                        </p>
                      ) : null}
                    </div>
                    <StatusPill tone={STATUS_TONE[change.status]}>
                      {t(`requestStatus.${change.status}`)}
                    </StatusPill>
                  </div>
                )
              }
              if (entry.source === 'leave') {
                const leave = entry.leave
                return (
                  <div key={`leave-${leave.id}`} className={styles.row}>
                    <div>
                      <p className={styles.rowType}>
                        {i18n.language.startsWith('pl') ? leave.labelPl : leave.labelEn}
                      </p>
                      <p className={styles.rowMeta}>
                        {submittedAt}
                        {` · ${formatDate(leave.dateFrom, 'dot')} – ${formatDate(leave.dateTo, 'dot')}`}
                        {` · ${t('portalRequests.leaveDays', { count: leave.days })}`}
                      </p>
                      {leave.decisionComment ? (
                        <p className={styles.rowNote}>
                          {t('portalRequests.decision', { text: leave.decisionComment })}
                        </p>
                      ) : null}
                    </div>
                    <StatusPill tone={LEAVE_TONE[leave.status] ?? 'warning'}>
                      {t(`portalRequestStatus.${leave.status as RequestStatus}`, {
                        defaultValue: leave.status,
                      })}
                    </StatusPill>
                  </div>
                )
              }
              const item = entry.item
              return (
                <div key={item.id} className={styles.row}>
                  <div>
                    <p className={styles.rowType}>{t(`requestTypes.${item.type}`)}</p>
                    <p className={styles.rowMeta}>
                      {submittedAt}
                      {item.dateFrom && item.dateTo
                        ? ` · ${formatDate(item.dateFrom, 'dot')} – ${formatDate(item.dateTo, 'dot')}`
                        : ''}
                    </p>
                    {/* Wniosek o członka rodziny: kogo dotyczy i czego — jak w widoku kadr (grupa 128). */}
                    {item.family ? (
                      <p className={styles.rowMeta}>
                        {t(`family.actions.${item.family.action}`)}
                        {item.family.memberLabel ? ` · ${item.family.memberLabel}` : ''}
                      </p>
                    ) : null}
                    {item.decisionComment ? (
                      <p className={styles.rowNote}>
                        {t('portalRequests.decision', { text: item.decisionComment })}
                      </p>
                    ) : null}
                  </div>
                  <StatusPill tone={STATUS_TONE[item.status]}>
                    {t(`portalRequestStatus.${item.status}`)}
                  </StatusPill>
                </div>
              )
            })}
          </Card>
        )}
      </section>

      <Modal
        open={form !== null}
        onClose={closeForm}
        title={t('portalRequests.formTitle', {
          type: form ? t(`requestTypes.${form.type}`) : '',
        })}
        footer={
          <div className={styles.modalActions}>
            <Button variant="secondary" onClick={closeForm}>
              {t('portalRequests.cancel')}
            </Button>
            <Button
              disabled={
                submit.isPending ||
                rangeInvalid ||
                (form?.needsDateRange === true && (dates.from === '' || dates.to === ''))
              }
              onClick={() => form && submit.mutate(form.type)}
            >
              {submit.isPending ? (
                <Spinner label={t('portalRequests.submitting')} />
              ) : (
                t('portalRequests.submit')
              )}
            </Button>
          </div>
        }
      >
        {error !== null ? <InfoBanner tone="danger">{t(error)}</InfoBanner> : null}
        {rangeInvalid ? <InfoBanner tone="danger">{t('portalRequests.errorRange')}</InfoBanner> : null}

        {form?.needsDateRange ? (
          <div className={styles.dates}>
            <FormField label={t('portalRequests.dateFrom')} htmlFor={ids.from}>
              <Input
                type="date"
                value={dates.from}
                onChange={(event) => setDates({ ...dates, from: event.target.value })}
              />
            </FormField>
            <FormField label={t('portalRequests.dateTo')} htmlFor={ids.to}>
              <Input
                type="date"
                value={dates.to}
                onChange={(event) => setDates({ ...dates, to: event.target.value })}
              />
            </FormField>
          </div>
        ) : null}

        <FormField label={t('portalRequests.note')} htmlFor={ids.note}>
          <textarea
            id={ids.note}
            className={styles.textarea}
            rows={3}
            placeholder={t('portalRequests.notePlaceholder')}
            value={note}
            onChange={(event) => setNote(event.target.value)}
          />
        </FormField>
      </Modal>

      <PenaltyObjectionModal penalty={objectionFor} onClose={() => setObjectionFor(null)} />

      <FamilyRequestModal
        open={familyOpen}
        onClose={() => setFamilyOpen(false)}
        members={familyMembers.data?.items ?? []}
        preselected={familyMemberId}
        onSubmitted={() => {
          void queryClient.invalidateQueries({ queryKey: ['my-requests'] })
        }}
      />

      {/* Wybór pola przy kafelku „Zmiana danych osobowych" (grupa 38). */}
      <Modal
        open={pickFields !== null}
        onClose={() => setPickFields(null)}
        title={t('portalRequests.pickFieldTitle')}
      >
        <p className={styles.subtitle}>{t('portalRequests.pickFieldLead')}</p>
        <div className={styles.tiles}>
          {(pickFields ?? []).map((field) => (
            <button
              key={field}
              type="button"
              className={styles.tile}
              onClick={() => {
                setPickFields(null)
                setChangeField(field)
              }}
            >
              <span>{t(`requestFields.${field}`)}</span>
              <span className={styles.rowMeta}>
                {changeFields.data?.values[field] ?? t('portalRequests.noValue')}
              </span>
            </button>
          ))}
        </div>
      </Modal>

      <LeaveRequestModal
        open={leaveType !== null}
        onClose={() => setLeaveType(null)}
        {...(leaveType === null ? {} : { initialTypeKey: leaveType })}
      />

      {changeField !== null && changeFields.data?.employeeId ? (
        <ChangeRequestModal
          open
          onClose={() => setChangeField(null)}
          employeeId={changeFields.data.employeeId}
          field={changeField}
          currentValue={changeFields.data.values[changeField] ?? null}
          onSubmitted={() => {
            void queryClient.invalidateQueries({ queryKey: ['my-change-fields'] })
            void queryClient.invalidateQueries({ queryKey: ['my-requests'] })
            void queryClient.invalidateQueries({ queryKey: ['me', 'dashboard'] })
          }}
        />
      ) : null}
    </>
  )
}
